import {
  type AddPlanItemInput,
  type AnswerClarifyInput,
  type ApplyPlanResult,
  type GeneratePlanInput,
  type PlanDraftView,
  type UpdatePlanItemInput,
  type WbsTemplateView,
} from '@apm/contracts';
import { apiFetch } from '../../lib/http';

export const planApi = {
  list: (projectId: string) => apiFetch<PlanDraftView[]>(`/projects/${projectId}/plans`),

  /**
   * Nháp đang chờ **bạn trả lời**, cắt ngang dự án — APM-UX-ai2.
   *
   * Cột trợ lý sống ngoài mọi route nên không có `projectId` để truyền; phạm vi
   * do repository chặn theo dự án người gọi là thành viên.
   */
  awaitingAnswer: () => apiFetch<PlanDraftView[]>('/plans/awaiting-answer'),

  generate: (input: GeneratePlanInput) =>
    apiFetch<PlanDraftView>('/plans/generate', { method: 'POST', body: input }),

  /** Trả lời vòng hỏi lại rồi sinh nháp — APM-806. */
  answer: (draftId: string, answers: AnswerClarifyInput['answers'], runId?: string) =>
    apiFetch<PlanDraftView>(`/plans/${draftId}/answers`, {
      method: 'POST',
      body: { answers, ...(runId ? { runId } : {}) },
    }),

  /** `documentIds` — tài liệu người dùng chỉ đích danh cho AI đọc (APM-ROLE-x2). */
  refine: (draftId: string, instruction: string, runId?: string, documentIds?: readonly string[]) =>
    apiFetch<PlanDraftView>(`/plans/${draftId}/refine`, {
      method: 'POST',
      body: {
        instruction,
        ...(runId ? { runId } : {}),
        ...(documentIds?.length ? { documentIds: [...documentIds] } : {}),
      },
    }),

  updateItem: (draftId: string, itemId: string, input: UpdatePlanItemInput) =>
    apiFetch<PlanDraftView>(`/plans/${draftId}/items/${itemId}`, {
      method: 'PATCH',
      body: input,
    }),

  addItem: (draftId: string, input: AddPlanItemInput) =>
    apiFetch<PlanDraftView>(`/plans/${draftId}/items`, { method: 'POST', body: input }),

  removeItem: (draftId: string, itemId: string) =>
    apiFetch<PlanDraftView>(`/plans/${draftId}/items/${itemId}`, { method: 'DELETE' }),

  /**
   * Áp đề xuất "ai làm việc nào" vào nháp — APM-UX-ai4. Chỉ chạm
   * `suggestedAssigneeId` của nháp; người phụ trách thật ra đời ở `apply`.
   */
  assign: (draftId: string, assignments: { itemId: string; userId: string }[]) =>
    apiFetch<PlanDraftView>(`/plans/${draftId}/assignees`, {
      method: 'POST',
      body: { assignments },
    }),

  apply: (draftId: string, startDate: string) =>
    apiFetch<ApplyPlanResult>(`/plans/${draftId}/apply`, {
      method: 'POST',
      body: { startDate },
    }),

  discard: (draftId: string) =>
    apiFetch<PlanDraftView>(`/plans/${draftId}/discard`, { method: 'POST' }),

  templates: () => apiFetch<WbsTemplateView[]>('/wbs-templates'),
};

export const planKeys = {
  list: (projectId: string) => ['plans', projectId] as const,
  awaitingAnswer: () => ['plans', 'awaiting-answer'] as const,
  templates: () => ['wbs-templates', false] as const,
};
